import { useGetIdentity, useNotification } from "@refinedev/core";
import { Card, Descriptions, Form, Input, Button, Tag } from "antd";
import { useState } from "react";

import { authClient, authErrorMessage } from "../auth-client";
import type { Me } from "../session";

const MIN_PASSWORD_LENGTH = 12;

type PasswordValues = { currentPassword: string; newPassword: string; confirm: string };

/** /account: own details and password (any staff session). */
export function AccountPage() {
  const { data: me } = useGetIdentity<Me>();
  const { open } = useNotification();
  const [form] = Form.useForm<PasswordValues>();
  const [busy, setBusy] = useState(false);

  async function changePassword(values: PasswordValues) {
    setBusy(true);
    const { error } = await authClient.changePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
      // Other devices must sign in again with the new password.
      revokeOtherSessions: true,
    });
    setBusy(false);
    if (error) {
      open?.({ type: "error", message: authErrorMessage(error) });
      return;
    }
    form.resetFields();
    open?.({ type: "success", message: "Şifren değişti. Diğer cihazlardaki oturumlar kapandı." });
  }

  return (
    <Card title="Hesabım" style={{ maxWidth: 560 }}>
      <Descriptions column={1} style={{ marginBottom: 24 }}>
        <Descriptions.Item label="Ad Soyad">{me?.name}</Descriptions.Item>
        <Descriptions.Item label="E-posta">{me?.email}</Descriptions.Item>
        <Descriptions.Item label="İki adımlı doğrulama">
          <Tag color="green">Açık</Tag>
        </Descriptions.Item>
      </Descriptions>
      <Form form={form} layout="vertical" requiredMark={false} onFinish={changePassword}>
        <Form.Item
          label="Mevcut şifre"
          name="currentPassword"
          rules={[{ required: true, message: "Mevcut şifreni gir." }]}
        >
          <Input.Password autoComplete="current-password" />
        </Form.Item>
        <Form.Item
          label="Yeni şifre"
          name="newPassword"
          rules={[
            { required: true, message: "Yeni şifre gir." },
            {
              min: MIN_PASSWORD_LENGTH,
              message: `Şifre en az ${MIN_PASSWORD_LENGTH} karakter olmalı.`,
            },
            { max: 128, message: "Şifre en fazla 128 karakter olabilir." },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          label="Yeni şifre (tekrar)"
          name="confirm"
          dependencies={["newPassword"]}
          rules={[
            { required: true, message: "Yeni şifreyi tekrar gir." },
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                !value || value === getFieldValue("newPassword")
                  ? Promise.resolve()
                  : Promise.reject(new Error("Şifreler aynı değil.")),
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={busy}>
          Şifreyi değiştir
        </Button>
      </Form>
    </Card>
  );
}
